/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { BreakRuleRequest } from '../models/break-rule-request';
import { BreakRuleWithUsageListItemResponse } from '../models/break-rule-with-usage-list-item-response';
import { CreateCustomerLocationRequest } from '../models/create-customer-location-request';
import { CreatedResponseWithBody } from '../models/created-response-with-body';
import { CustomerLocationUpdateRequest } from '../models/customer-location-update-request';
import { DocumentUploadRequest } from '../models/document-upload-request';
import { DutyModelRequest } from '../models/duty-model-request';
import { DutyModelWithAdoptingDepartmentsListItemResponse } from '../models/duty-model-with-adopting-departments-list-item-response';
import { EmptySuccessfulResponse } from '../models/empty-successful-response';
import { GeneralApiResponse } from '../models/general-api-response';
import { ImportUploadResponse } from '../models/import-upload-response';
import { ListApiResponse } from '../models/list-api-response';
import { LocationListItemResponse } from '../models/location-list-item-response';
import { LocationResponse } from '../models/location-response';
import { RoundingRuleRequest } from '../models/rounding-rule-request';
import { RoundingRuleWithUsageListItemResponse } from '../models/rounding-rule-with-usage-list-item-response';

@Injectable({
  providedIn: 'root',
})
export class CustomerLocationService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation postCustomerLocationActivate
   */
  static readonly PostCustomerLocationActivatePath = '/api/web/v1/customers/{customer}/locations/{location}/activation';

  /**
   * Activate customer location, accessible only for users with role 'ROLE_CUSTOMER_ADMIN'
   * or 'ROLE_CUSTOMER_LOCATION_ADMIN'.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postCustomerLocationActivate()` instead.
   *
   * This method doesn't expect any request body.
   */
  postCustomerLocationActivate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<EmptySuccessfulResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.PostCustomerLocationActivatePath, 'post');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptySuccessfulResponse>;
      })
    );
  }

  /**
   * Activate customer location, accessible only for users with role 'ROLE_CUSTOMER_ADMIN'
   * or 'ROLE_CUSTOMER_LOCATION_ADMIN'.
   *
   *
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `postCustomerLocationActivate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  postCustomerLocationActivate(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
  }
): Observable<EmptySuccessfulResponse> {

    return this.postCustomerLocationActivate$Response(params).pipe(
      map((r: StrictHttpResponse<EmptySuccessfulResponse>) => r.body as EmptySuccessfulResponse)
    );
  }

  /**
   * Path part for operation deleteCustomerLocationDeactivate
   */
  static readonly DeleteCustomerLocationDeactivatePath = '/api/web/v1/customers/{customer}/locations/{location}/activation';

  /**
   * Deactivate customer location, accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN'
   * or 'ROLE_CUSTOMER_LOCATION_ADMIN'.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteCustomerLocationDeactivate()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCustomerLocationDeactivate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<EmptySuccessfulResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.DeleteCustomerLocationDeactivatePath, 'delete');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptySuccessfulResponse>;
      })
    );
  }

  /**
   * Deactivate customer location, accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN'
   * or 'ROLE_CUSTOMER_LOCATION_ADMIN'.
   *
   *
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteCustomerLocationDeactivate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCustomerLocationDeactivate(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
  }
): Observable<EmptySuccessfulResponse> {

    return this.deleteCustomerLocationDeactivate$Response(params).pipe(
      map((r: StrictHttpResponse<EmptySuccessfulResponse>) => r.body as EmptySuccessfulResponse)
    );
  }

  /**
   * Path part for operation postCustomerLocationBreakRulesActivate
   */
  static readonly PostCustomerLocationBreakRulesActivatePath = '/api/web/v1/customers/{customer}/locations/{location}/break-rules/{breakRule}/activation';

  /**
   * Activate a break rule of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postCustomerLocationBreakRulesActivate()` instead.
   *
   * This method doesn't expect any request body.
   */
  postCustomerLocationBreakRulesActivate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Break rule Id.
     */
    breakRule: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<EmptySuccessfulResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.PostCustomerLocationBreakRulesActivatePath, 'post');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
      rb.path('breakRule', params.breakRule, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptySuccessfulResponse>;
      })
    );
  }

  /**
   * Activate a break rule of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `postCustomerLocationBreakRulesActivate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  postCustomerLocationBreakRulesActivate(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Break rule Id.
     */
    breakRule: number;
    context?: HttpContext
  }
): Observable<EmptySuccessfulResponse> {

    return this.postCustomerLocationBreakRulesActivate$Response(params).pipe(
      map((r: StrictHttpResponse<EmptySuccessfulResponse>) => r.body as EmptySuccessfulResponse)
    );
  }

  /**
   * Path part for operation deleteCustomerLocationBreakRulesDeactivate
   */
  static readonly DeleteCustomerLocationBreakRulesDeactivatePath = '/api/web/v1/customers/{customer}/locations/{location}/break-rules/{breakRule}/activation';

  /**
   * Deactivate a break rule of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteCustomerLocationBreakRulesDeactivate()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCustomerLocationBreakRulesDeactivate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Break rule Id.
     */
    breakRule: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<EmptySuccessfulResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.DeleteCustomerLocationBreakRulesDeactivatePath, 'delete');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
      rb.path('breakRule', params.breakRule, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptySuccessfulResponse>;
      })
    );
  }

  /**
   * Deactivate a break rule of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteCustomerLocationBreakRulesDeactivate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCustomerLocationBreakRulesDeactivate(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Break rule Id.
     */
    breakRule: number;
    context?: HttpContext
  }
): Observable<EmptySuccessfulResponse> {

    return this.deleteCustomerLocationBreakRulesDeactivate$Response(params).pipe(
      map((r: StrictHttpResponse<EmptySuccessfulResponse>) => r.body as EmptySuccessfulResponse)
    );
  }

  /**
   * Path part for operation getCustomerLocationBreakRulesList
   */
  static readonly GetCustomerLocationBreakRulesListPath = '/api/web/v1/customers/{customer}/locations/{location}/break-rules';

  /**
   * List the break rules of a customer location.
   *
   * Returns all rules of the location including inactive ones — hiding
   * inactive rules (e.g. in the duty-model break-rule dropdown) is a
   * frontend concern.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCustomerLocationBreakRulesList()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerLocationBreakRulesList$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ListApiResponse & {
'data'?: Array<BreakRuleWithUsageListItemResponse>;
}>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.GetCustomerLocationBreakRulesListPath, 'get');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ListApiResponse & {
        'data'?: Array<BreakRuleWithUsageListItemResponse>;
        }>;
      })
    );
  }

  /**
   * List the break rules of a customer location.
   *
   * Returns all rules of the location including inactive ones — hiding
   * inactive rules (e.g. in the duty-model break-rule dropdown) is a
   * frontend concern.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCustomerLocationBreakRulesList$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerLocationBreakRulesList(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
  }
): Observable<ListApiResponse & {
'data'?: Array<BreakRuleWithUsageListItemResponse>;
}> {

    return this.getCustomerLocationBreakRulesList$Response(params).pipe(
      map((r: StrictHttpResponse<ListApiResponse & {
'data'?: Array<BreakRuleWithUsageListItemResponse>;
}>) => r.body as ListApiResponse & {
'data'?: Array<BreakRuleWithUsageListItemResponse>;
})
    );
  }

  /**
   * Path part for operation postCustomerLocationBreakRulesCreate
   */
  static readonly PostCustomerLocationBreakRulesCreatePath = '/api/web/v1/customers/{customer}/locations/{location}/break-rules';

  /**
   * Create a break rule for a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postCustomerLocationBreakRulesCreate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  postCustomerLocationBreakRulesCreate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
    body: BreakRuleRequest
  }
): Observable<StrictHttpResponse<{
'message'?: string;
'data'?: {
'id'?: number;
};
}>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.PostCustomerLocationBreakRulesCreatePath, 'post');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<{
        'message'?: string;
        'data'?: {
        'id'?: number;
        };
        }>;
      })
    );
  }

  /**
   * Create a break rule for a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `postCustomerLocationBreakRulesCreate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  postCustomerLocationBreakRulesCreate(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
    body: BreakRuleRequest
  }
): Observable<{
'message'?: string;
'data'?: {
'id'?: number;
};
}> {

    return this.postCustomerLocationBreakRulesCreate$Response(params).pipe(
      map((r: StrictHttpResponse<{
'message'?: string;
'data'?: {
'id'?: number;
};
}>) => r.body as {
'message'?: string;
'data'?: {
'id'?: number;
};
})
    );
  }

  /**
   * Path part for operation putCustomerLocationBreakRulesUpdate
   */
  static readonly PutCustomerLocationBreakRulesUpdatePath = '/api/web/v1/customers/{customer}/locations/{location}/break-rules/{breakRule}';

  /**
   * Update a break rule of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `putCustomerLocationBreakRulesUpdate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  putCustomerLocationBreakRulesUpdate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Break rule Id.
     */
    breakRule: number;
    context?: HttpContext
    body: BreakRuleRequest
  }
): Observable<StrictHttpResponse<EmptySuccessfulResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.PutCustomerLocationBreakRulesUpdatePath, 'put');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
      rb.path('breakRule', params.breakRule, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptySuccessfulResponse>;
      })
    );
  }

  /**
   * Update a break rule of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `putCustomerLocationBreakRulesUpdate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  putCustomerLocationBreakRulesUpdate(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Break rule Id.
     */
    breakRule: number;
    context?: HttpContext
    body: BreakRuleRequest
  }
): Observable<EmptySuccessfulResponse> {

    return this.putCustomerLocationBreakRulesUpdate$Response(params).pipe(
      map((r: StrictHttpResponse<EmptySuccessfulResponse>) => r.body as EmptySuccessfulResponse)
    );
  }

  /**
   * Path part for operation deleteCustomerLocationBreakRulesDelete
   */
  static readonly DeleteCustomerLocationBreakRulesDeletePath = '/api/web/v1/customers/{customer}/locations/{location}/break-rules/{breakRule}';

  /**
   * Delete a break rule of a customer location. A rule referenced by a duty model cannot be deleted.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteCustomerLocationBreakRulesDelete()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCustomerLocationBreakRulesDelete$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Break rule Id.
     */
    breakRule: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<EmptySuccessfulResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.DeleteCustomerLocationBreakRulesDeletePath, 'delete');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
      rb.path('breakRule', params.breakRule, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptySuccessfulResponse>;
      })
    );
  }

  /**
   * Delete a break rule of a customer location. A rule referenced by a duty model cannot be deleted.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteCustomerLocationBreakRulesDelete$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCustomerLocationBreakRulesDelete(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Break rule Id.
     */
    breakRule: number;
    context?: HttpContext
  }
): Observable<EmptySuccessfulResponse> {

    return this.deleteCustomerLocationBreakRulesDelete$Response(params).pipe(
      map((r: StrictHttpResponse<EmptySuccessfulResponse>) => r.body as EmptySuccessfulResponse)
    );
  }

  /**
   * Path part for operation getCustomerLocationList
   */
  static readonly GetCustomerLocationListPath = '/api/web/v1/customers/{customer}/locations';

  /**
   * Get list of Customer Locations.
   *
   * **Accessible only for users with permissions 'FEATURE_VIEW_LOCATIONS',
   * 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_USER', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCustomerLocationList()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerLocationList$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Filter active or inactive locations
     */
    active?: any;

    /**
     * Search customer locations by name or by department name.
     */
    search?: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ListApiResponse & {
'data'?: Array<LocationListItemResponse>;
}>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.GetCustomerLocationListPath, 'get');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.query('active', params.active, {"style":"form"});
      rb.query('search', params.search, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ListApiResponse & {
        'data'?: Array<LocationListItemResponse>;
        }>;
      })
    );
  }

  /**
   * Get list of Customer Locations.
   *
   * **Accessible only for users with permissions 'FEATURE_VIEW_LOCATIONS',
   * 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_USER', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCustomerLocationList$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerLocationList(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Filter active or inactive locations
     */
    active?: any;

    /**
     * Search customer locations by name or by department name.
     */
    search?: string;
    context?: HttpContext
  }
): Observable<ListApiResponse & {
'data'?: Array<LocationListItemResponse>;
}> {

    return this.getCustomerLocationList$Response(params).pipe(
      map((r: StrictHttpResponse<ListApiResponse & {
'data'?: Array<LocationListItemResponse>;
}>) => r.body as ListApiResponse & {
'data'?: Array<LocationListItemResponse>;
})
    );
  }

  /**
   * Path part for operation postCustomerLocationCreate
   */
  static readonly PostCustomerLocationCreatePath = '/api/web/v1/customers/{customer}/locations';

  /**
   * Create customer location, accessible only for users with permission 'ROLE_CUSTOMER_ADMIN'.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postCustomerLocationCreate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  postCustomerLocationCreate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;
    context?: HttpContext
    body: CreateCustomerLocationRequest
  }
): Observable<StrictHttpResponse<CreatedResponseWithBody>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.PostCustomerLocationCreatePath, 'post');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CreatedResponseWithBody>;
      })
    );
  }

  /**
   * Create customer location, accessible only for users with permission 'ROLE_CUSTOMER_ADMIN'.
   *
   *
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `postCustomerLocationCreate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  postCustomerLocationCreate(params: {

    /**
     * Customer Id.
     */
    customer: number;
    context?: HttpContext
    body: CreateCustomerLocationRequest
  }
): Observable<CreatedResponseWithBody> {

    return this.postCustomerLocationCreate$Response(params).pipe(
      map((r: StrictHttpResponse<CreatedResponseWithBody>) => r.body as CreatedResponseWithBody)
    );
  }

  /**
   * Path part for operation postCustomerLocationDutyModelsActivate
   */
  static readonly PostCustomerLocationDutyModelsActivatePath = '/api/web/v1/customers/{customer}/locations/{location}/duty-models/{dutyModel}/activation';

  /**
   * Activate a duty model of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postCustomerLocationDutyModelsActivate()` instead.
   *
   * This method doesn't expect any request body.
   */
  postCustomerLocationDutyModelsActivate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Duty model Id.
     */
    dutyModel: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<EmptySuccessfulResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.PostCustomerLocationDutyModelsActivatePath, 'post');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
      rb.path('dutyModel', params.dutyModel, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptySuccessfulResponse>;
      })
    );
  }

  /**
   * Activate a duty model of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `postCustomerLocationDutyModelsActivate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  postCustomerLocationDutyModelsActivate(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Duty model Id.
     */
    dutyModel: number;
    context?: HttpContext
  }
): Observable<EmptySuccessfulResponse> {

    return this.postCustomerLocationDutyModelsActivate$Response(params).pipe(
      map((r: StrictHttpResponse<EmptySuccessfulResponse>) => r.body as EmptySuccessfulResponse)
    );
  }

  /**
   * Path part for operation deleteCustomerLocationDutyModelsDeactivate
   */
  static readonly DeleteCustomerLocationDutyModelsDeactivatePath = '/api/web/v1/customers/{customer}/locations/{location}/duty-models/{dutyModel}/activation';

  /**
   * Deactivate a duty model of a customer location.
   *
   * Deactivation changes nothing else: the validity window, days and rule
   * references stay untouched, and historical references stay valid.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteCustomerLocationDutyModelsDeactivate()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCustomerLocationDutyModelsDeactivate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Duty model Id.
     */
    dutyModel: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<EmptySuccessfulResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.DeleteCustomerLocationDutyModelsDeactivatePath, 'delete');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
      rb.path('dutyModel', params.dutyModel, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptySuccessfulResponse>;
      })
    );
  }

  /**
   * Deactivate a duty model of a customer location.
   *
   * Deactivation changes nothing else: the validity window, days and rule
   * references stay untouched, and historical references stay valid.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteCustomerLocationDutyModelsDeactivate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCustomerLocationDutyModelsDeactivate(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Duty model Id.
     */
    dutyModel: number;
    context?: HttpContext
  }
): Observable<EmptySuccessfulResponse> {

    return this.deleteCustomerLocationDutyModelsDeactivate$Response(params).pipe(
      map((r: StrictHttpResponse<EmptySuccessfulResponse>) => r.body as EmptySuccessfulResponse)
    );
  }

  /**
   * Path part for operation getCustomerLocationDutyModelsList
   */
  static readonly GetCustomerLocationDutyModelsListPath = '/api/web/v1/customers/{customer}/locations/{location}/duty-models';

  /**
   * List the duty models of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCustomerLocationDutyModelsList()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerLocationDutyModelsList$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ListApiResponse & {
'data'?: Array<DutyModelWithAdoptingDepartmentsListItemResponse>;
}>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.GetCustomerLocationDutyModelsListPath, 'get');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ListApiResponse & {
        'data'?: Array<DutyModelWithAdoptingDepartmentsListItemResponse>;
        }>;
      })
    );
  }

  /**
   * List the duty models of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCustomerLocationDutyModelsList$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerLocationDutyModelsList(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
  }
): Observable<ListApiResponse & {
'data'?: Array<DutyModelWithAdoptingDepartmentsListItemResponse>;
}> {

    return this.getCustomerLocationDutyModelsList$Response(params).pipe(
      map((r: StrictHttpResponse<ListApiResponse & {
'data'?: Array<DutyModelWithAdoptingDepartmentsListItemResponse>;
}>) => r.body as ListApiResponse & {
'data'?: Array<DutyModelWithAdoptingDepartmentsListItemResponse>;
})
    );
  }

  /**
   * Path part for operation postCustomerLocationDutyModelsCreate
   */
  static readonly PostCustomerLocationDutyModelsCreatePath = '/api/web/v1/customers/{customer}/locations/{location}/duty-models';

  /**
   * Create a duty model for a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postCustomerLocationDutyModelsCreate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  postCustomerLocationDutyModelsCreate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
    body: DutyModelRequest
  }
): Observable<StrictHttpResponse<{
'message'?: string;
'data'?: {
'id'?: number;
};
}>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.PostCustomerLocationDutyModelsCreatePath, 'post');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<{
        'message'?: string;
        'data'?: {
        'id'?: number;
        };
        }>;
      })
    );
  }

  /**
   * Create a duty model for a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `postCustomerLocationDutyModelsCreate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  postCustomerLocationDutyModelsCreate(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
    body: DutyModelRequest
  }
): Observable<{
'message'?: string;
'data'?: {
'id'?: number;
};
}> {

    return this.postCustomerLocationDutyModelsCreate$Response(params).pipe(
      map((r: StrictHttpResponse<{
'message'?: string;
'data'?: {
'id'?: number;
};
}>) => r.body as {
'message'?: string;
'data'?: {
'id'?: number;
};
})
    );
  }

  /**
   * Path part for operation putCustomerLocationDutyModelsUpdate
   */
  static readonly PutCustomerLocationDutyModelsUpdatePath = '/api/web/v1/customers/{customer}/locations/{location}/duty-models/{dutyModel}';

  /**
   * Update a duty model of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `putCustomerLocationDutyModelsUpdate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  putCustomerLocationDutyModelsUpdate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Duty model Id.
     */
    dutyModel: number;
    context?: HttpContext
    body: DutyModelRequest
  }
): Observable<StrictHttpResponse<EmptySuccessfulResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.PutCustomerLocationDutyModelsUpdatePath, 'put');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
      rb.path('dutyModel', params.dutyModel, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptySuccessfulResponse>;
      })
    );
  }

  /**
   * Update a duty model of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `putCustomerLocationDutyModelsUpdate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  putCustomerLocationDutyModelsUpdate(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Duty model Id.
     */
    dutyModel: number;
    context?: HttpContext
    body: DutyModelRequest
  }
): Observable<EmptySuccessfulResponse> {

    return this.putCustomerLocationDutyModelsUpdate$Response(params).pipe(
      map((r: StrictHttpResponse<EmptySuccessfulResponse>) => r.body as EmptySuccessfulResponse)
    );
  }

  /**
   * Path part for operation deleteCustomerLocationDutyModelsDelete
   */
  static readonly DeleteCustomerLocationDutyModelsDeletePath = '/api/web/v1/customers/{customer}/locations/{location}/duty-models/{dutyModel}';

  /**
   * Delete a duty model of a customer location.
   *
   * Removes the model together with its day rows. A model that calculated a
   * recorded time record cannot be deleted.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteCustomerLocationDutyModelsDelete()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCustomerLocationDutyModelsDelete$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Duty model Id.
     */
    dutyModel: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<EmptySuccessfulResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.DeleteCustomerLocationDutyModelsDeletePath, 'delete');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
      rb.path('dutyModel', params.dutyModel, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptySuccessfulResponse>;
      })
    );
  }

  /**
   * Delete a duty model of a customer location.
   *
   * Removes the model together with its day rows. A model that calculated a
   * recorded time record cannot be deleted.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteCustomerLocationDutyModelsDelete$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCustomerLocationDutyModelsDelete(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Duty model Id.
     */
    dutyModel: number;
    context?: HttpContext
  }
): Observable<EmptySuccessfulResponse> {

    return this.deleteCustomerLocationDutyModelsDelete$Response(params).pipe(
      map((r: StrictHttpResponse<EmptySuccessfulResponse>) => r.body as EmptySuccessfulResponse)
    );
  }

  /**
   * Path part for operation postCustomerLocationImportProcess
   */
  static readonly PostCustomerLocationImportProcessPath = '/api/web/v1/customers/{customer}/locations/imports/{import}';

  /**
   * Process a previously uploaded CustomerLocation import: create locations from the
   * stored file, touching (updating the timestamp of) any location that already exists —
   * matched by name + legal name within the customer.
   *
   * The import is addressed by its UUID — a non-guessable handle that prevents enumeration
   * of import records on top of the customer-scoped authorization.
   *
   * **Accessible only for customer admins of the customer in the URL**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postCustomerLocationImportProcess()` instead.
   *
   * This method doesn't expect any request body.
   */
  postCustomerLocationImportProcess$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;
    import: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.PostCustomerLocationImportProcessPath, 'post');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('import', params.import, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Process a previously uploaded CustomerLocation import: create locations from the
   * stored file, touching (updating the timestamp of) any location that already exists —
   * matched by name + legal name within the customer.
   *
   * The import is addressed by its UUID — a non-guessable handle that prevents enumeration
   * of import records on top of the customer-scoped authorization.
   *
   * **Accessible only for customer admins of the customer in the URL**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `postCustomerLocationImportProcess$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  postCustomerLocationImportProcess(params: {

    /**
     * Customer Id.
     */
    customer: number;
    import: string;
    context?: HttpContext
  }
): Observable<void> {

    return this.postCustomerLocationImportProcess$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation postCustomerLocationImportUpload
   */
  static readonly PostCustomerLocationImportUploadPath = '/api/web/v1/customers/{customer}/locations/imports';

  /**
   * Upload and validate a filled CustomerLocation import template (.xlsx or .csv).
   *
   * Validates every row up front: any error returns 400 with the full error list and
   * nothing is stored. An already-existing location (same name + legal name) is NOT an
   * upload error — it is a process-time duplicate whose timestamp gets touched. On success
   * the file is saved and an import record is created (unprocessed) for a later
   * processing call.
   *
   * **Accessible only for customer admins of the customer in the URL**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postCustomerLocationImportUpload()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  postCustomerLocationImportUpload$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;
    context?: HttpContext

    /**
     * The filled import template. Field 'file' holds an .xlsx or .csv file.
     */
    body: DocumentUploadRequest
  }
): Observable<StrictHttpResponse<{
'message'?: string;
'data'?: ImportUploadResponse;
}>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.PostCustomerLocationImportUploadPath, 'post');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<{
        'message'?: string;
        'data'?: ImportUploadResponse;
        }>;
      })
    );
  }

  /**
   * Upload and validate a filled CustomerLocation import template (.xlsx or .csv).
   *
   * Validates every row up front: any error returns 400 with the full error list and
   * nothing is stored. An already-existing location (same name + legal name) is NOT an
   * upload error — it is a process-time duplicate whose timestamp gets touched. On success
   * the file is saved and an import record is created (unprocessed) for a later
   * processing call.
   *
   * **Accessible only for customer admins of the customer in the URL**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `postCustomerLocationImportUpload$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  postCustomerLocationImportUpload(params: {

    /**
     * Customer Id.
     */
    customer: number;
    context?: HttpContext

    /**
     * The filled import template. Field 'file' holds an .xlsx or .csv file.
     */
    body: DocumentUploadRequest
  }
): Observable<{
'message'?: string;
'data'?: ImportUploadResponse;
}> {

    return this.postCustomerLocationImportUpload$Response(params).pipe(
      map((r: StrictHttpResponse<{
'message'?: string;
'data'?: ImportUploadResponse;
}>) => r.body as {
'message'?: string;
'data'?: ImportUploadResponse;
})
    );
  }

  /**
   * Path part for operation getCustomerLocationImportTemplate
   */
  static readonly GetCustomerLocationImportTemplatePath = '/api/web/v1/customers/{customer}/locations/import-template';

  /**
   * Download the CustomerLocation import template (.xlsx, header row only).
   *
   * **Accessible only for customer admins of the customer in the URL**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCustomerLocationImportTemplate()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerLocationImportTemplate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Blob>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.GetCustomerLocationImportTemplatePath, 'get');
    if (params) {
      rb.path('customer', params.customer, {});
    }

    return this.http.request(rb.build({
      responseType: 'blob',
      accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Blob>;
      })
    );
  }

  /**
   * Download the CustomerLocation import template (.xlsx, header row only).
   *
   * **Accessible only for customer admins of the customer in the URL**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCustomerLocationImportTemplate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerLocationImportTemplate(params: {

    /**
     * Customer Id.
     */
    customer: number;
    context?: HttpContext
  }
): Observable<Blob> {

    return this.getCustomerLocationImportTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<Blob>) => r.body as Blob)
    );
  }

  /**
   * Path part for operation postCustomerLocationRoundingRulesActivate
   */
  static readonly PostCustomerLocationRoundingRulesActivatePath = '/api/web/v1/customers/{customer}/locations/{location}/rounding-rules/{roundingRule}/activation';

  /**
   * Activate a rounding rule of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postCustomerLocationRoundingRulesActivate()` instead.
   *
   * This method doesn't expect any request body.
   */
  postCustomerLocationRoundingRulesActivate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Rounding rule Id.
     */
    roundingRule: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<EmptySuccessfulResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.PostCustomerLocationRoundingRulesActivatePath, 'post');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
      rb.path('roundingRule', params.roundingRule, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptySuccessfulResponse>;
      })
    );
  }

  /**
   * Activate a rounding rule of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `postCustomerLocationRoundingRulesActivate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  postCustomerLocationRoundingRulesActivate(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Rounding rule Id.
     */
    roundingRule: number;
    context?: HttpContext
  }
): Observable<EmptySuccessfulResponse> {

    return this.postCustomerLocationRoundingRulesActivate$Response(params).pipe(
      map((r: StrictHttpResponse<EmptySuccessfulResponse>) => r.body as EmptySuccessfulResponse)
    );
  }

  /**
   * Path part for operation deleteCustomerLocationRoundingRulesDeactivate
   */
  static readonly DeleteCustomerLocationRoundingRulesDeactivatePath = '/api/web/v1/customers/{customer}/locations/{location}/rounding-rules/{roundingRule}/activation';

  /**
   * Deactivate a rounding rule of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteCustomerLocationRoundingRulesDeactivate()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCustomerLocationRoundingRulesDeactivate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Rounding rule Id.
     */
    roundingRule: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<EmptySuccessfulResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.DeleteCustomerLocationRoundingRulesDeactivatePath, 'delete');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
      rb.path('roundingRule', params.roundingRule, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptySuccessfulResponse>;
      })
    );
  }

  /**
   * Deactivate a rounding rule of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteCustomerLocationRoundingRulesDeactivate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCustomerLocationRoundingRulesDeactivate(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Rounding rule Id.
     */
    roundingRule: number;
    context?: HttpContext
  }
): Observable<EmptySuccessfulResponse> {

    return this.deleteCustomerLocationRoundingRulesDeactivate$Response(params).pipe(
      map((r: StrictHttpResponse<EmptySuccessfulResponse>) => r.body as EmptySuccessfulResponse)
    );
  }

  /**
   * Path part for operation getCustomerLocationRoundingRulesList
   */
  static readonly GetCustomerLocationRoundingRulesListPath = '/api/web/v1/customers/{customer}/locations/{location}/rounding-rules';

  /**
   * List the rounding rules of a customer location.
   *
   * Returns all rules of the location including inactive ones — hiding
   * inactive rules (e.g. in the duty-model rounding-rule dropdown) is a
   * frontend concern.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCustomerLocationRoundingRulesList()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerLocationRoundingRulesList$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ListApiResponse & {
'data'?: Array<RoundingRuleWithUsageListItemResponse>;
}>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.GetCustomerLocationRoundingRulesListPath, 'get');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ListApiResponse & {
        'data'?: Array<RoundingRuleWithUsageListItemResponse>;
        }>;
      })
    );
  }

  /**
   * List the rounding rules of a customer location.
   *
   * Returns all rules of the location including inactive ones — hiding
   * inactive rules (e.g. in the duty-model rounding-rule dropdown) is a
   * frontend concern.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCustomerLocationRoundingRulesList$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerLocationRoundingRulesList(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
  }
): Observable<ListApiResponse & {
'data'?: Array<RoundingRuleWithUsageListItemResponse>;
}> {

    return this.getCustomerLocationRoundingRulesList$Response(params).pipe(
      map((r: StrictHttpResponse<ListApiResponse & {
'data'?: Array<RoundingRuleWithUsageListItemResponse>;
}>) => r.body as ListApiResponse & {
'data'?: Array<RoundingRuleWithUsageListItemResponse>;
})
    );
  }

  /**
   * Path part for operation postCustomerLocationRoundingRulesCreate
   */
  static readonly PostCustomerLocationRoundingRulesCreatePath = '/api/web/v1/customers/{customer}/locations/{location}/rounding-rules';

  /**
   * Create a rounding rule for a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postCustomerLocationRoundingRulesCreate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  postCustomerLocationRoundingRulesCreate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
    body: RoundingRuleRequest
  }
): Observable<StrictHttpResponse<{
'message'?: string;
'data'?: {
'id'?: number;
};
}>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.PostCustomerLocationRoundingRulesCreatePath, 'post');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<{
        'message'?: string;
        'data'?: {
        'id'?: number;
        };
        }>;
      })
    );
  }

  /**
   * Create a rounding rule for a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `postCustomerLocationRoundingRulesCreate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  postCustomerLocationRoundingRulesCreate(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
    body: RoundingRuleRequest
  }
): Observable<{
'message'?: string;
'data'?: {
'id'?: number;
};
}> {

    return this.postCustomerLocationRoundingRulesCreate$Response(params).pipe(
      map((r: StrictHttpResponse<{
'message'?: string;
'data'?: {
'id'?: number;
};
}>) => r.body as {
'message'?: string;
'data'?: {
'id'?: number;
};
})
    );
  }

  /**
   * Path part for operation putCustomerLocationRoundingRulesUpdate
   */
  static readonly PutCustomerLocationRoundingRulesUpdatePath = '/api/web/v1/customers/{customer}/locations/{location}/rounding-rules/{roundingRule}';

  /**
   * Update a rounding rule of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `putCustomerLocationRoundingRulesUpdate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  putCustomerLocationRoundingRulesUpdate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Rounding rule Id.
     */
    roundingRule: number;
    context?: HttpContext
    body: RoundingRuleRequest
  }
): Observable<StrictHttpResponse<EmptySuccessfulResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.PutCustomerLocationRoundingRulesUpdatePath, 'put');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
      rb.path('roundingRule', params.roundingRule, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptySuccessfulResponse>;
      })
    );
  }

  /**
   * Update a rounding rule of a customer location.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `putCustomerLocationRoundingRulesUpdate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  putCustomerLocationRoundingRulesUpdate(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Rounding rule Id.
     */
    roundingRule: number;
    context?: HttpContext
    body: RoundingRuleRequest
  }
): Observable<EmptySuccessfulResponse> {

    return this.putCustomerLocationRoundingRulesUpdate$Response(params).pipe(
      map((r: StrictHttpResponse<EmptySuccessfulResponse>) => r.body as EmptySuccessfulResponse)
    );
  }

  /**
   * Path part for operation deleteCustomerLocationRoundingRulesDelete
   */
  static readonly DeleteCustomerLocationRoundingRulesDeletePath = '/api/web/v1/customers/{customer}/locations/{location}/rounding-rules/{roundingRule}';

  /**
   * Delete a rounding rule of a customer location. A rule referenced by a duty model cannot be deleted.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteCustomerLocationRoundingRulesDelete()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCustomerLocationRoundingRulesDelete$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Rounding rule Id.
     */
    roundingRule: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<EmptySuccessfulResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.DeleteCustomerLocationRoundingRulesDeletePath, 'delete');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
      rb.path('roundingRule', params.roundingRule, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptySuccessfulResponse>;
      })
    );
  }

  /**
   * Delete a rounding rule of a customer location. A rule referenced by a duty model cannot be deleted.
   *
   * **Accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteCustomerLocationRoundingRulesDelete$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCustomerLocationRoundingRulesDelete(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;

    /**
     * Rounding rule Id.
     */
    roundingRule: number;
    context?: HttpContext
  }
): Observable<EmptySuccessfulResponse> {

    return this.deleteCustomerLocationRoundingRulesDelete$Response(params).pipe(
      map((r: StrictHttpResponse<EmptySuccessfulResponse>) => r.body as EmptySuccessfulResponse)
    );
  }

  /**
   * Path part for operation getCustomerLocationGet
   */
  static readonly GetCustomerLocationGetPath = '/api/web/v1/customers/{customer}/locations/{location}';

  /**
   * GET Customer Location.
   *
   * **Accessible only for users with permissions 'FEATURE_VIEW_LOCATIONS',
   * 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_USER', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCustomerLocationGet()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerLocationGet$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<GeneralApiResponse & {
'data'?: LocationResponse;
}>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.GetCustomerLocationGetPath, 'get');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<GeneralApiResponse & {
        'data'?: LocationResponse;
        }>;
      })
    );
  }

  /**
   * GET Customer Location.
   *
   * **Accessible only for users with permissions 'FEATURE_VIEW_LOCATIONS',
   * 'ROLE_CUSTOMER_ADMIN', 'ROLE_CUSTOMER_USER', 'ROLE_CUSTOMER_LOCATION_ADMIN'**
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCustomerLocationGet$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCustomerLocationGet(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
  }
): Observable<GeneralApiResponse & {
'data'?: LocationResponse;
}> {

    return this.getCustomerLocationGet$Response(params).pipe(
      map((r: StrictHttpResponse<GeneralApiResponse & {
'data'?: LocationResponse;
}>) => r.body as GeneralApiResponse & {
'data'?: LocationResponse;
})
    );
  }

  /**
   * Path part for operation putCustomerLocationUpdate
   */
  static readonly PutCustomerLocationUpdatePath = '/api/web/v1/customers/{customer}/locations/{location}';

  /**
   * Update customer location, accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN'
   * or 'ROLE_CUSTOMER_LOCATION_ADMIN'.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `putCustomerLocationUpdate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  putCustomerLocationUpdate$Response(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
    body: CustomerLocationUpdateRequest
  }
): Observable<StrictHttpResponse<EmptySuccessfulResponse>> {

    const rb = new RequestBuilder(this.rootUrl, CustomerLocationService.PutCustomerLocationUpdatePath, 'put');
    if (params) {
      rb.path('customer', params.customer, {});
      rb.path('location', params.location, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmptySuccessfulResponse>;
      })
    );
  }

  /**
   * Update customer location, accessible only for users with permissions 'ROLE_CUSTOMER_ADMIN'
   * or 'ROLE_CUSTOMER_LOCATION_ADMIN'.
   *
   *
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `putCustomerLocationUpdate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  putCustomerLocationUpdate(params: {

    /**
     * Customer Id.
     */
    customer: number;

    /**
     * Location Id.
     */
    location: number;
    context?: HttpContext
    body: CustomerLocationUpdateRequest
  }
): Observable<EmptySuccessfulResponse> {

    return this.putCustomerLocationUpdate$Response(params).pipe(
      map((r: StrictHttpResponse<EmptySuccessfulResponse>) => r.body as EmptySuccessfulResponse)
    );
  }

}
